import {
  faHouseBuilding,
  faUsers,
  faGraduationCap,
  faHandHoldingHeart,
  faTrophyStar,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition, IconProp } from '@fortawesome/fontawesome-svg-core';
import { SelectorOptionsProps } from 'shared/design-system/components/Selector';
import { SectionIndicators } from '../IndicatorDetails/utils';
import { ReportPeriod } from '../SectionPeriod/utils';

export interface Indicator {
  id: number;
  dbId?: number;
  name: string;
  seen: boolean;
  stageId: number;
  customizable?: boolean;
  unit?: string | '{{beneficiaryTerm}}';
  definitionStart: string;
  definitionEnd: string;
  initialForecastStart: string;
  initialForecastEnd: string;
  initialMeasurementStart: string;
  initialMeasurementEnd: string;
  outcome?: string;
  impactReceiptText?: string;
  forecastOutcome?: string;
  finalOutcome?: string;
  finalForecastStart: string;
  finalForecastEnd: string;
  isHuman: boolean;
  finalMeasurementStart: string;
  finalMeasurementEnd: string;
  definition: string;
  stgName: string;
  version: 'v3' | 'v4' | null;
}

export interface IndicatorGroup {
  id: number;
  name: string;
  indicators: Indicator[];
}

export interface Service {
  dbId: number;
  name: string;
  goal: string;
  description: string;
  version?: 'v3' | 'v4' | null;
  indicators: Indicator[];
}

export interface ServiceGroup {
  dbId: number;
  description: string;
  name: string;
  services: Service[];
}

export interface IndicatorStagesProps {
  isDebug: boolean;
  handleBack: () => unknown;
  handleNext: (
    pressedButton?: boolean,
    forcedValue?: string | string[],
  ) => unknown;
  handleChange: (value: string | string[]) => unknown;
  reloadAnswers: () => unknown;
  beneficiaryTerm?: string;
  programName: string;
  intermediaries?: string;
  serviceGoal?: string;
  serviceType?: string;
  reportPeriod: ReportPeriod;
  isImpactSummaryCompleted?: boolean;
  isReportPassed: boolean;
  choiceIsSelected: boolean;
  indicatorSummary: string[] | IndicatorByService;
  pageSectionProps: {
    title: string;
    subTitle: React.ReactNode;
    helpText: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    error: any;
    extraClass: string;
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageOptionProps: SelectorOptionsProps<any>;
  questionId: number;
  title: string;
  helpText: string;
  reportId: number;
  service: Service; // selected service
  services: Service[];
  indicators: IndicatorGroup[];
  capacityDevelopmentEnabled: boolean;
  learnEnabled: boolean;
  actEnabled: boolean;
  prevQuestion: number;
  userAnswerId: number | undefined;
}

const indicatorSectionKeysWithoutLearnAct = [
  'capacityDevelopment',
  'reach',
  'succeed',
  'catalyzed',
] as const;
const indicatorSectionKeys = [
  ...indicatorSectionKeysWithoutLearnAct,
  'learn',
  'act',
] as const;

export const indicatorSectionsInOrder: IndicatorSectionKey[] = [
  'capacityDevelopment',
  'reach',
  'learn',
  'act',
  'catalyzed',
  'succeed',
];

export type IndicatorSectionKey = typeof indicatorSectionKeys[number];

export interface IndicatorHeaderList {
  stageId: number;
  sectionKey: IndicatorSectionKey;
  text:
    | 'capacity development'
    | 'reach'
    | 'learn'
    | 'act'
    | 'succeed'
    | 'catalyzed';
  icon: IconProp | IconDefinition;
}

export const INDICATOR_STAGES: { [x in IndicatorSectionKey]: number } = {
  learn: 1,
  act: 2,
  succeed: 3,
  catalyzed: 4,
  reach: 5,
  capacityDevelopment: 6,
} as const;

export const INDICATOR_STAGES_KEY: { [x: number]: IndicatorSectionKey } = {
  1: 'learn',
  2: 'act',
  3: 'succeed',
  4: 'catalyzed',
  5: 'reach',
  6: 'capacityDevelopment',
} as const;

export const stageIdToString = (stage: IndicatorSectionKey): string => {
  switch (stage) {
    case 'capacityDevelopment':
      return 'Program Development';
    case 'act':
      return 'Act';
    case 'catalyzed':
      return 'Catalyzed';
    case 'learn':
      return 'Learn';
    case 'reach':
      return 'Reach';
    case 'succeed':
      return 'Succeed';
    default:
      return stage;
  }
};

export interface IndicatorWithUpdate extends Indicator {
  lockDelete?: boolean;
  updatedText?: string;
}

export type IndicatorByService = {
  [x in IndicatorSectionKey]: IndicatorWithUpdate[];
};

export const getPreviousAndNextStage = (
  indicatorStageList: IndicatorHeaderList[],
  indicator: IndicatorHeaderList,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
): { previous?: IndicatorHeaderList; next?: IndicatorHeaderList } => {
  const index = indicatorStageList.findIndex(i => i.text === indicator.text);
  if (index < 0) return {};
  return {
    previous: index === 0 ? undefined : indicatorStageList[index - 1],
    next:
      index === indicatorStageList.length - 1
        ? undefined
        : indicatorStageList[index + 1],
  };
};

interface IndicatorValidations {
  isValid: boolean;
  isAnySeen: boolean;
  isAllSeen: boolean;
}

export const allIndicatorsValid = (
  indicators: IndicatorWithUpdate[],
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
  sectionKey: IndicatorSectionKey,
): IndicatorValidations => {
  let isAllSeen = true;
  let isAnySeen = false;
  if (
    indicators.length === 0 &&
    (sectionKey === 'capacityDevelopment' ||
      sectionKey === 'learn' ||
      sectionKey === 'act')
  ) {
    return {
      isAllSeen: false,
      isAnySeen: true,
      isValid: true,
    };
  }
  for (const i of indicators) {
    if (
      !capacityDevelopmentEnabled &&
      i.stageId === INDICATOR_STAGES.capacityDevelopment
    ) {
      continue;
    }
    if (!learnEnabled && i.stageId === INDICATOR_STAGES.learn) {
      continue;
    }
    if (!actEnabled && i.stageId === INDICATOR_STAGES.act) {
      continue;
    }
    if (!i.updatedText && i.customizable) {
      return { isValid: false, isAnySeen, isAllSeen };
    }
    if (i.seen) {
      isAnySeen = true;
    } else {
      isAllSeen = false;
    }
  }
  return { isValid: true, isAnySeen, isAllSeen };
};

export const isIndicatorListValid = (
  indicators: IndicatorWithUpdate[],
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
  sectionKey: IndicatorSectionKey,
): IndicatorValidations => {
  if (
    indicators.length === 0 &&
    (sectionKey === 'reach' || sectionKey === 'succeed')
  ) {
    return { isValid: false, isAnySeen: false, isAllSeen: false };
  }
  return allIndicatorsValid(
    indicators,
    capacityDevelopmentEnabled,
    learnEnabled,
    actEnabled,
    sectionKey,
  );
};

export const validateAllIndicators = (
  selectedIndicators: IndicatorByService | undefined,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
): boolean => {
  for (const indicatorKey in selectedIndicators) {
    const key = indicatorKey as IndicatorSectionKey;
    const indicators = selectedIndicators[key];
    const validations = allIndicatorsValid(
      indicators,
      capacityDevelopmentEnabled,
      learnEnabled,
      actEnabled,
      key,
    );
    if (!validations.isValid) {
      return false;
    }
    switch (key) {
      // case 'learn':
      case 'reach':
      case 'succeed':
        if (indicators.length === 0) return false;
        break;
      // case 'capacityDevelopment':
      case 'catalyzed':
        break;
    }
  }
  return true;
};

export const getInitialSelectedIndicatorList = (
  initialData: IndicatorStagesProps['indicatorSummary'],
  allIndicators: IndicatorByService,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
  firstSection: IndicatorSectionKey,
): { selected: IndicatorByService; rest: IndicatorByService } => {
  const selected: IndicatorByService = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };
  const selectedKeys: { [x: number]: boolean | undefined } = {};
  const hasData = Array.isArray(initialData) && initialData.length !== 0;

  if (hasData) {
    indicatorSectionKeysWithoutLearnAct.forEach(key => {
      const selectedIndicators: IndicatorWithUpdate[] = [];
      allIndicators[key].forEach(indicator => {
        if (indicator.version === 'v3') return;
        if (initialData.includes(indicator.name)) {
          // avoid duplicates
          selectedIndicators.findIndex(x => x.id === indicator.id) === -1 &&
            selectedIndicators.push(indicator);
          selectedKeys[indicator.id] = true;
        }
      });
      selected[key] = selectedIndicators;
    });
  }

  const firstSectionIndicator = allIndicators[firstSection][0];
  if (selected[firstSection].length === 0 && firstSectionIndicator) {
    selected[firstSection] = [firstSectionIndicator];
    selectedKeys[firstSectionIndicator.id] = true;
  }
  const firstReachIndicator = allIndicators.reach[0];
  if (selected.reach.length === 0 && firstReachIndicator) {
    selected.reach = [firstReachIndicator];
    selectedKeys[firstReachIndicator.id] = true;
  }

  selected.learn = allIndicators.learn;
  selected.act = allIndicators.act;

  const rest: IndicatorByService = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };

  indicatorSectionKeysWithoutLearnAct.forEach(key => {
    allIndicators[key].forEach(indicator => {
      if (!selectedKeys[indicator.id]) {
        rest[key].push(indicator);
      }
    });
  });
  return { selected, rest };
};

export const getIndicatorById = (
  indicatorGroups: IndicatorGroup[],
  serviceId: number,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
): IndicatorByService => {
  const result: IndicatorByService = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };
  const uniqueIndicators: {
    [indicatorId in IndicatorSectionKey]: {
      [id: string]: true | undefined;
    };
  } = {
    capacityDevelopment: {},
    reach: {},
    learn: {},
    act: {},
    catalyzed: {},
    succeed: {},
  };
  for (const group of indicatorGroups) {
    group.indicators.forEach(indicator => {
      const stageKey = INDICATOR_STAGES_KEY[indicator.stageId];
      if (
        indicator.version !== 'v4' ||
        uniqueIndicators[stageKey][indicator.name]
      ) {
        return;
      }
      if (group.id === serviceId) {
        uniqueIndicators[stageKey][indicator.name] = true;
        result[INDICATOR_STAGES_KEY[indicator.stageId]]?.push(indicator);
      }
    });
  }
  if (!capacityDevelopmentEnabled) {
    result.capacityDevelopment = [];
  }
  if (!learnEnabled) {
    result.learn = [];
  }
  if (!actEnabled) {
    result.act = [];
  }
  return result;
};

export const indicatorStageList: IndicatorHeaderList[] = [
  {
    icon: faHouseBuilding,
    text: 'capacity development',
    stageId: INDICATOR_STAGES.capacityDevelopment,
    sectionKey: 'capacityDevelopment',
  },
  {
    icon: faUsers,
    text: 'reach',
    stageId: INDICATOR_STAGES.reach,
    sectionKey: 'reach',
  },
  {
    icon: faGraduationCap,
    text: 'learn',
    stageId: INDICATOR_STAGES.learn,
    sectionKey: 'learn',
  },
  {
    icon: faHandHoldingHeart,
    text: 'act',
    stageId: INDICATOR_STAGES.act,
    sectionKey: 'act',
  },
  {
    icon: faTrophyStar,
    text: 'succeed',
    stageId: INDICATOR_STAGES.succeed,
    sectionKey: 'succeed',
  },
];

export const indicatorStageHeaderList = (
  indicatorsByService: IndicatorByService,
  capacityDevelopmentEnabled?: boolean,
  learnEnabled?: boolean,
  actEnabled?: boolean,
): IndicatorHeaderList[] => {
  const result: IndicatorHeaderList[] = [];
  indicatorStageList.forEach(s => {
    if (
      capacityDevelopmentEnabled === false &&
      s.sectionKey === 'capacityDevelopment'
    ) {
      return;
    }
    if (learnEnabled === false && s.sectionKey === 'learn') {
      return;
    }
    if (actEnabled === false && s.sectionKey === 'act') {
      return;
    }
    if (indicatorsByService[s.sectionKey].length) {
      result.push(s);
    }
  });
  return result;
};

export const isCapacityDevelopmentEnabled = (
  capacity: string | string[] | undefined,
): boolean => {
  return capacity
    ? typeof capacity === 'string'
      ? capacity.length > 2
      : capacity.length > 0
    : false;
};

const requiredIndicatorSections: IndicatorSectionKey[] = ['reach', 'succeed'];

export const isImpactDetailsCompleted = (
  impactDetails: SectionIndicators | undefined,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
  isReportPassed: boolean,
): boolean => {
  if (!impactDetails) return false;

  for (const key of indicatorSectionsInOrder) {
    if (requiredIndicatorSections.includes(key)) {
      if (impactDetails[key].length === 0) {
        return false;
      }
    }
    for (const indicator of impactDetails[key]) {
      if (!isReportPassed) {
        if (!indicator.forecast) {
          return false;
        }
      } else {
        if (!indicator.finalForecast) {
          return false;
        }
      }
      if (!indicator.measurement) {
        return false;
      }
    }
  }
  return true;
};

export const isImpactDetailsValid = (
  impactSummary: IndicatorByService | undefined,
  impactDetails: SectionIndicators | undefined,
): boolean => {
  if (!impactSummary || !impactDetails) return false;

  for (const key of indicatorSectionsInOrder) {
    if (
      !impactSummary[key] ||
      !impactDetails[key] ||
      impactSummary[key].length !== impactDetails[key].length
    ) {
      return false;
    }
  }
  return true;
};

const staledData = 'Yes, This Summary is Accurate';
export const isImpactSummaryAndDetailsValid = (
  impactSummary: string | IndicatorByService | undefined,
  impactDetails: SectionIndicators | string | undefined,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
) => {
  const summary =
    typeof impactSummary === 'string' && impactSummary !== staledData
      ? JSON.parse(impactSummary)
      : impactSummary;
  const isImpactSummaryValid =
    impactSummary && impactSummary !== staledData
      ? validateAllIndicators(
          summary,
          capacityDevelopmentEnabled,
          learnEnabled,
          actEnabled,
        )
      : false;

  return {
    isImpactSummaryValid,
    isImpactDetailsValid: impactDetails
      ? isImpactDetailsValid(
          summary,
          typeof impactDetails === 'string'
            ? JSON.parse(impactDetails)
            : impactDetails,
        )
      : false,
  };
};
